<template>
  <modal @close-modal="emit('close-modal')">
    <main class="container pt-md-0 export-bets">
      <h3 class="mt-0 mb-1 py-0 text-center">{{ title }}</h3>
      <h6 class="my-0 py-0 mb-3 font-weight-normal text-center subtitle">
        Pick any date range.
      </h6>

      <form @submit.prevent="exportCsv">
        <div class="row">
          <div class="form-group col-12 col-md-6">
            <label for="exportFromDate">From</label>
            <input
              id="exportFromDate"
              v-model="fromDate"
              type="date"
              :max="today"
              class="form-control"
            />
          </div>
          <div class="form-group col-12 col-md-6">
            <label for="exportToDate">To</label>
            <input
              id="exportToDate"
              v-model="toDate"
              type="date"
              :max="today"
              class="form-control"
            />
          </div>
        </div>

        <div v-if="errorMessage" class="error-block mt-2" role="alert">
          {{ errorMessage }}
        </div>
        <div v-else-if="emptyMessage" class="empty-block mt-2">
          {{ emptyMessage }}
        </div>

        <div class="row mt-3">
          <div class="col-12">
            <button type="submit" class="export-btn" :disabled="submitDisabled">
              <font-awesome-icon
                v-if="!exporting"
                :icon="faFileExport"
                class="mr-2"
              />
              <span v-if="exporting">Exporting...</span>
              <span v-else>Export CSV</span>
            </button>
          </div>
        </div>
      </form>
    </main>
  </modal>
</template>

<script setup>
import { computed, ref } from 'vue';
import dayjs from 'dayjs';
import { useAPI } from '@/composables/useAPI';
import { useUserStore } from '@/stores/user';
import { buildCsv, downloadCsv } from '@/utils/csvExport';
import { BET_CSV_HEADERS, buildBetCsvRows } from '@/utils/bettingCsv';

import Modal from '@/components/global/Modal.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faFileExport } from '@fortawesome/free-solid-svg-icons/faFileExport';

const userStore = useUserStore();

const props = defineProps({
  player: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close-modal']);

const today = dayjs().format('YYYY-MM-DD');
const toDate = ref(today);
const fromDate = ref(dayjs().subtract(6, 'day').format('YYYY-MM-DD'));
const exporting = ref(false);
const emptyMessage = ref('');

const isPlayerExport = computed(() => !!(props.player && props.player.id));

const title = computed(() => {
  if (isPlayerExport.value && props.player.username) {
    return `Export Results: ${props.player.username}`;
  }
  return 'Export Graded Bets';
});

const validationError = computed(() => {
  if (!fromDate.value || !toDate.value) {
    return 'Please choose a from and to date.';
  }
  const from = dayjs(fromDate.value);
  const to = dayjs(toDate.value);
  if (!from.isValid() || !to.isValid()) {
    return 'Please choose valid dates.';
  }
  if (from.isAfter(to)) {
    return 'The "from" date must be on or before the "to" date.';
  }
  return '';
});

const errorMessage = computed(() => validationError.value);

const submitDisabled = computed(
  () => exporting.value || !!validationError.value,
);

const { request: getExportBets } = useAPI('/user/getExportBets');
const { request: getPlayersExportBets } = useAPI('/user/getPlayersExportBets');

function fetchExportSessions(fromStart, toEnd) {
  const range = {
    fromDate: fromStart.toISOString(),
    toDate: toEnd.toISOString(),
  };
  if (isPlayerExport.value) {
    return getPlayersExportBets({
      playerID: props.player.id,
      ...range,
    });
  }
  return getExportBets(range);
}

function safeFilenameSegment(value) {
  return String(value || '')
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, '_');
}

async function exportCsv() {
  emptyMessage.value = '';
  if (validationError.value) return;

  const fromStart = dayjs(fromDate.value).startOf('day');
  const toEnd = dayjs(toDate.value).endOf('day');

  exporting.value = true;
  try {
    const response = await fetchExportSessions(fromStart, toEnd);
    const sessions = response?.sessions ?? [];

    const inRange = sessions.filter((session) => {
      const start = session?.game?.start;
      if (!start) return false;
      const t = dayjs(start);
      return (
        (t.isAfter(fromStart) || t.isSame(fromStart)) &&
        (t.isBefore(toEnd) || t.isSame(toEnd))
      );
    });

    const commissionRate = isPlayerExport.value
      ? props.player.commissionCharged
      : userStore.user.commissionCharged;
    const rows = buildBetCsvRows(inRange, commissionRate);

    if (rows.length === 0) {
      emptyMessage.value = 'No graded bets in this range.';
      return;
    }

    const csv = buildCsv(rows, BET_CSV_HEADERS);
    const usernamePart = isPlayerExport.value
      ? `-${safeFilenameSegment(props.player.username) || props.player.id}`
      : '';
    const filename = `4casters-bets${usernamePart}-${fromDate.value}-to-${toDate.value}.csv`;
    downloadCsv(filename, csv);
    emit('close-modal');
  } catch (error) {
    console.error('Error exporting graded bets', error);
    // Server guards (range too wide, too many bets, rate limit) return
    // actionable messages — show those; anything else gets the generic copy.
    const isGuardError = error?.status === 400 || error?.status === 429;
    emptyMessage.value =
      isGuardError && error.message
        ? error.message
        : 'Something went wrong while exporting. Please try again.';
  } finally {
    exporting.value = false;
  }
}
</script>

<style scoped lang="scss">
@use 'sass:map';
@import '@/scss/variables.scss';
@import '@/scss/theme/variables.scss';

.export-bets {
  h3 {
    color: $black;
    font-weight: 700;
    letter-spacing: -0.01em;
  }
}

.subtitle {
  color: $black-203;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 0.75rem;

  label {
    font-size: $font-size-sm;
    font-weight: 600;
    color: $blue-201;
    margin-bottom: 6px;
  }

  input.form-control {
    width: 100%;
    background: $white_02-1;
    border: $border-02;
    color: $black;
    padding: 0.625rem 0.75rem;
    border-radius: 8px;
    font-size: $font-size-base;
    // Native date picker: hint the browser so the calendar icon and
    // popover render legibly in light mode (overridden for night below).
    color-scheme: light;
    transition:
      border-color 0.15s ease,
      box-shadow 0.15s ease;

    &:hover {
      border-color: $color-primary;
    }

    &:focus {
      outline: none;
      border-color: $color-primary;
      box-shadow: 0 0 0 3px $blue-1201;
    }

    @media screen and (max-width: map.get($grid-breakpoints, md)) {
      font-size: 16px;
    }
  }

  [theme='night'] & input.form-control {
    // Makes the native calendar-picker icon and popover legible on the
    // dark input background.
    color-scheme: dark;
  }
}

.error-block {
  color: $red-400;
  background: $grey-500;
  border: $border-02;
  border-radius: 8px;
  padding: 0.625rem 0.75rem;
  font-size: $font-size-sm;
}

.empty-block {
  color: $black-203;
  background: $grey-500;
  border: $border-02;
  border-radius: 8px;
  padding: 0.625rem 0.75rem;
  font-size: $font-size-sm;
  text-align: center;
}

.export-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 8px;
  background: $color-primary;
  color: $white;
  font-weight: bold;
  font-size: $font-size-base;
  cursor: pointer;
  transition:
    background 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.05s ease;

  &:hover:not(:disabled) {
    box-shadow: $box-shadow-04;
    filter: brightness(1.08);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px $blue-1201;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
</style>
